import { useEffect, useMemo, useState } from 'react';

import { useGameConfig } from '../../config/GameConfigProvider';
import { crossOriginCommunicationController } from '../../controllers/crossOriginCommunication/CrossOriginCommunicationControllerConnector';
import { OutgoingMessage } from '../../controllers/crossOriginCommunication/types';
import { DeviceInfo } from '../../deviceInfo/deviceInfo';
import { useMedia } from '../../hooks/useMedia';
import LottieController from '../../lazyLibraries/LottieController';
import { CoreSessionStorage } from '../../storage/session/CoreSessionStorage';
import { useCasinoConfigFetcher } from '../../store/apiHooks/useCasinoConfigFetcher';
import { useEnvFetcher } from '../../store/apiHooks/useEnvFetcher';
import { usePlayerConfigFetcher } from '../../store/apiHooks/usePlayerConfigFetcher';
import { usePromotionFetcher } from '../../store/apiHooks/usePromotionFetcher';
import { useRtpFetcher } from '../../store/apiHooks/useRtpFetcher';
import { useStatsConfigFetcher } from '../../store/apiHooks/useStatsConfigFetcher';
import { useStatsSettingsFetcher } from '../../store/apiHooks/useStatsSettingsFetcher';
import { useTableConfigFetcher } from '../../store/apiHooks/useTableConfigFetcher';
import { useTableLevelMessagesFetcher } from '../../store/apiHooks/useTableLevelMessagesFetcher';
import { useUserInfoFetcher } from '../../store/apiHooks/useUserInfoFetcher';
import { useVersionsFetcher } from '../../store/apiHooks/useVersionsFetcher';
import { useCoreDispatch } from '../../store/CoreStore';
import { useBetSlice } from '../../store/slices/bets/selectors';
import { betActions } from '../../store/slices/bets/slice';
import { useChipSlice } from '../../store/slices/chips/selectors';
import { useConfigSlice } from '../../store/slices/config/selectors';
import { configActions } from '../../store/slices/config/slice';
import { useGameStateSlice } from '../../store/slices/game/selectors';
import { gameStateActions } from '../../store/slices/game/slice';
import { useInitializationSlice } from '../../store/slices/initialization/selectors';
import { initializationActions } from '../../store/slices/initialization/slice';
import { popupActions } from '../../store/slices/popup/slice';
import { useSettingsSlice } from '../../store/slices/settings/selectors';
import { settingsActions } from '../../store/slices/settings/slice';
import { Curtain } from '../curtain/Curtain';
import { useDragAndDropInit } from '../dragAndDrop/useDragAndDropInit';
import { PopupType } from '../popup/types';

import { useCurtain } from './useCurtain';
import { useFullscreen } from './useFullscreen';

export const GameInitializer = (): null => {
  const dispatch = useCoreDispatch();

  DeviceInfo.init();

  const casinoConfig = useConfigSlice.casinoConfig();
  const chipsReady = useChipSlice.getAreChipsReady();
  const sessionLanguage = useConfigSlice.session()?.lang;
  const tableId = useConfigSlice.tableId();
  const tableConfig = useConfigSlice.tableConfig();
  const userInfoReady = useGameStateSlice.userInfoReady();
  const playerConfig = useConfigSlice.playerConfig();
  const isBetsOpenStatus = useGameStateSlice.isBetsOpenStatus();
  const totalBet = useBetSlice.totalBet();
  const [loadFailed, setLoadFailed] = useState(false);

  const {
    config: { betConfig, defaultTimings, playAreaSize, tableConfigOptions, uiVolumeChannel, volumeChannels, languages },
  } = useGameConfig();

  /* Language */
  useEffect(() => {
    dispatch(settingsActions.setLanguage({ languages, sessionLanguage }));
  }, [dispatch, languages, sessionLanguage]);

  /* Session storage */
  useEffect(() => {
    const savedBets = tableId ? CoreSessionStorage.getLastBets(tableId) : null;
    if (savedBets) {
      dispatch(betActions.initLastBets({ bets: savedBets }));
    }
  }, [tableId, dispatch]);

  /* Timings */
  useEffect(() => {
    dispatch(configActions.setDefaultTimings(defaultTimings));
  }, [defaultTimings, dispatch]);

  /* Handle game close, exit, leave, quit */
  useEffect(() => {
    const handleGameCloseExitLeaveQuit = (): void => {
      if (casinoConfig?.notifyCloseContainer) {
        crossOriginCommunicationController.dispatch(OutgoingMessage.CloseContainer);
      }

      if (casinoConfig?.returnMainPage) {
        crossOriginCommunicationController.dispatch(OutgoingMessage.GameQuit);
      }
    };
    window.addEventListener('pagehide', handleGameCloseExitLeaveQuit);

    return () => {
      window.removeEventListener('pagehide', handleGameCloseExitLeaveQuit);
    };
  }, [casinoConfig]);

  /* Fetchers */
  const casinoConfigFetcher = useCasinoConfigFetcher(true);
  useEffect(() => {
    casinoConfigFetcher();
  }, [casinoConfigFetcher]);

  const envFetcher = useEnvFetcher(true);
  useEffect(() => {
    envFetcher();
  }, [envFetcher]);

  const playerConfigFetcher = usePlayerConfigFetcher(true);
  useEffect(() => {
    // Fetch player config after chips are initialised to apply selected chip from config based on available chips
    // See chips/slice for more details
    if (chipsReady) {
      playerConfigFetcher();
    }
  }, [chipsReady, playerConfigFetcher]);

  const promotionFetcher = usePromotionFetcher();
  useEffect(() => {
    promotionFetcher();
  }, [promotionFetcher]);

  const rtpFetcher = useRtpFetcher(false);
  useEffect(() => {
    rtpFetcher();
  }, [rtpFetcher]);

  const statsConfigFetcher = useStatsConfigFetcher();
  useEffect(() => {
    statsConfigFetcher();
  }, [statsConfigFetcher]);

  const statsSettingsFetcher = useStatsSettingsFetcher();
  useEffect(() => {
    statsSettingsFetcher();
  }, [statsSettingsFetcher]);

  const dealerNameReady = useGameStateSlice.dealer()?.name;
  const tableNameReady = useConfigSlice.tableName();
  const tableLevelMessagesFetcher = useTableLevelMessagesFetcher(false);
  useEffect(() => {
    if (tableNameReady && dealerNameReady) {
      tableLevelMessagesFetcher();
    }
  }, [dealerNameReady, tableLevelMessagesFetcher, tableNameReady]);

  const tableConfigFetcher = useTableConfigFetcher(tableConfigOptions, true);
  useEffect(() => {
    tableConfigFetcher();
  }, [tableConfigFetcher]);

  const userInfoFetcher = useUserInfoFetcher(true);
  useEffect(() => {
    if (tableConfig && casinoConfig && playerConfig) {
      userInfoFetcher();
    }
  }, [casinoConfig, playerConfig, tableConfig, userInfoFetcher]);

  const versionsFetcher = useVersionsFetcher(true);
  useEffect(() => {
    versionsFetcher();
  }, [versionsFetcher]);

  /* Set populate store with game config */
  useEffect(() => {
    if (tableConfig) {
      dispatch(betActions.setBetLimits(tableConfig.betLimits));
      dispatch(betActions.setTableLimits(tableConfig.tableBetLimit));

      if (tableConfig.openTime > 0) {
        dispatch(gameStateActions.setTable({ openTime: tableConfig.openTime }));
      }
    }
  }, [dispatch, tableConfig]);

  useEffect(() => {
    dispatch(settingsActions.setGameVolumeChannels(Object.values(volumeChannels)));
    dispatch(settingsActions.setUiVolumeChannel(uiVolumeChannel));
    dispatch(configActions.setPlayAreaSize(playAreaSize));
    dispatch(betActions.initBetStack(betConfig));
  }, [dispatch, betConfig, playAreaSize, uiVolumeChannel, volumeChannels]);

  const videoStream = useConfigSlice.videoStream();
  useEffect(() => {
    if (videoStream === false) {
      dispatch(popupActions.pushPopup(PopupType.VideoIsDisabled));
    }
  }, [dispatch, videoStream]);

  const areBlockingResourcesReady = useInitializationSlice.getAreBlockingResourcesReady();
  const areVolumeChannelsReady = useSettingsSlice.getVolumeChannelsReady(Object.values(volumeChannels));
  const betsReady = useBetSlice.betsReady();
  const envReady = useConfigSlice.env();
  const isSubscribed = useGameStateSlice.getGameSubscribed();

  const progress = useMemo(() => {
    const loading = [
      !!envReady,
      !!playerConfig,
      !!tableConfig,
      areBlockingResourcesReady,
      areVolumeChannelsReady,
      betsReady,
      chipsReady,
      isSubscribed,
      userInfoReady,
    ];
    return Math.round(
      loading.reduce((value: number, currentValue: boolean) => {
        if (currentValue) {
          value += 100 / loading.length;
        }
        return value;
      }, 0)
    );
  }, [
    areBlockingResourcesReady,
    areVolumeChannelsReady,
    betsReady,
    chipsReady,
    envReady,
    isSubscribed,
    playerConfig,
    tableConfig,
    userInfoReady,
  ]);
  useEffect(() => {
    switch (progress) {
      case 100:
        if (!loadFailed) {
          dispatch(initializationActions.setIsReadyToRender(true));
          crossOriginCommunicationController.dispatch(OutgoingMessage.GameLoadEnd);
          crossOriginCommunicationController.dispatch(OutgoingMessage.UpdateSettings);
          crossOriginCommunicationController.dispatch(OutgoingMessage.UpdateWin);
          crossOriginCommunicationController.dispatch(OutgoingMessage.UpdateBets);
        }
        break;
      case 0:
        break;
      default:
        crossOriginCommunicationController.dispatch(OutgoingMessage.GameLoadProgress, { progress });
        break;
    }
  }, [dispatch, progress, loadFailed]);

  const minBetSize = useBetSlice.getMinBet();
  const balance = useGameStateSlice.balance();

  useEffect(() => {
    if (tableConfig?.tableClosed) {
      setLoadFailed(true);
      Curtain.resurrect();
    }
  }, [dispatch, tableConfig]);

  const curtainVisible = useCurtain(progress);
  useEffect(() => {
    if (userInfoReady && tableConfig && !curtainVisible) {
      /**
       * Display low balance popup on load if balance is not available
       * or is less than min bet size and bets are open and total placed bet is 0.
       */
      if (!balance || (balance.amount < minBetSize && balance.bonus < minBetSize && isBetsOpenStatus && !totalBet)) {
        dispatch(popupActions.pushPopup(PopupType.InsufficientFunds));
      }
    }
  }, [balance, curtainVisible, dispatch, isBetsOpenStatus, minBetSize, tableConfig, totalBet, userInfoReady]);

  const isWide = useMedia('(min-width: 1024px)');
  useEffect(() => {
    if (!isWide) {
      LottieController.timeoutInit();
    }
  }, [isWide]);

  useDragAndDropInit();
  useFullscreen();

  return null;
};
